import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Reusable hook to handle transient UI highlight states triggered by custom events or imperative calls.
 * 
 * @param {Object} options
 * @param {string} [options.eventName] - Custom window event name to listen for
 * @param {Function} [options.filter] - (event) => boolean filter function
 * @param {number} [options.durationMs=5000] - Duration in ms before highlight auto-clears
 * @param {string} [options.defaultMessage=''] - Default message for the highlight
 * @param {Function} [options.onTrigger] - Callback executed when highlight triggers
 */
export function useTransientHighlight({
    eventName,
    filter,
    durationMs = 5000,
    defaultMessage = '',
    onTrigger
} = {}) {
    const [isHighlighted, setIsHighlighted] = useState(false);
    const [message, setMessage] = useState(defaultMessage);
    const timerRef = useRef(null);

    const clearHighlight = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        setIsHighlighted(false);
    }, []);

    const triggerHighlight = useCallback((customMsg) => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        setMessage(customMsg || defaultMessage);
        setIsHighlighted(true);

        if (onTrigger) {
            onTrigger();
        }

        if (durationMs > 0) {
            timerRef.current = setTimeout(() => {
                setIsHighlighted(false);
            }, durationMs);
        }
    }, [defaultMessage, durationMs, onTrigger]);

    useEffect(() => {
        if (!eventName) return;

        const handleEvent = (event) => {
            if (!filter || filter(event)) {
                triggerHighlight(event?.detail?.message);
            }
        };

        window.addEventListener(eventName, handleEvent);
        return () => {
            window.removeEventListener(eventName, handleEvent);
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [eventName, filter, triggerHighlight]);

    return {
        isHighlighted,
        message,
        triggerHighlight,
        clearHighlight
    };
}

export default useTransientHighlight;
